import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { Activity, User, Box, Clock, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 15;

const Logs: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const { data } = await api.get('/audit-logs');
        setLogs(data);
      } catch (err) {
        console.error("Falha ao carregar logs");
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  // Pesquisa em todos os campos relevantes.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      [l.email, l.action, l.target_type, l.target_id, l.details]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [logs, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageLogs = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Volta à 1.ª página sempre que a pesquisa muda.
  useEffect(() => { setPage(1); }, [search]);

  const getActionColor = (action: string) => {
    if (action.includes('CREATE') || action.includes('ACCEPT')) return 'text-emerald-500 bg-emerald-500/10';
    if (action.includes('DELETE') || action.includes('REJECT')) return 'text-red-500 bg-red-500/10';
    if (action.includes('UPDATE')) return 'text-blue-500 bg-blue-500/10';
    return 'text-slate-500 bg-slate-500/10';
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 py-10 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-600/10 text-blue-600 rounded-2xl"><Activity size={22} /></div>
          <div>
            <h2 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Auditoria</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-0.5">
              {loading ? 'A carregar…' : `${filtered.length} ${filtered.length === 1 ? 'registo' : 'registos'}`}
            </p>
          </div>
        </div>

        <div className="relative w-full md:w-[320px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Pesquisar (utilizador, ação, alvo…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all text-slate-700 dark:text-slate-200 font-medium placeholder:text-slate-400 placeholder:font-normal"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="p-4 text-xs font-medium text-slate-400">Data / hora</th>
                <th className="p-4 text-xs font-medium text-slate-400">Utilizador</th>
                <th className="p-4 text-xs font-medium text-slate-400">Ação</th>
                <th className="p-4 text-xs font-medium text-slate-400">Alvo</th>
                <th className="p-4 text-xs font-medium text-slate-400">Detalhes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {loading ? (
                <tr><td colSpan={5} className="p-16 text-center animate-pulse text-slate-400">A ler registos…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="p-16 text-center text-slate-400">{search ? 'Nenhum registo corresponde à pesquisa.' : 'Nenhuma atividade registada.'}</td></tr>
              ) : pageLogs.map((log, idx) => (
                  <tr key={log.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                    <td className="p-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                            <Clock size={14} className="text-slate-400" />
                            <span>{new Date(log.timestamp).toLocaleString('pt-PT')}</span>
                        </div>
                    </td>
                    <td className="p-4">
                        <div className="flex items-center gap-2">
                            <User size={14} className="text-slate-400" />
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{log.email}</span>
                        </div>
                    </td>
                    <td className="p-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${getActionColor(log.action)}`}>
                            {log.action}
                        </span>
                    </td>
                    <td className="p-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                            <Box size={14} className="text-slate-400" />
                            <span>{log.target_type || '—'}</span>
                            {log.target_id && <span className="text-xs text-slate-400 font-mono">#{log.target_id.slice(0,8)}</span>}
                        </div>
                    </td>
                    <td className="p-4 text-sm text-slate-400 max-w-xs truncate">
                        {log.details || '—'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">Página {currentPage} de {totalPages}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Seguinte <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;
